/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import { LinksGroupSection } from 'views/motorcycle_rental/components/LinksGroupSection';
import { Box } from '@material-ui/core';
import { withTranslation } from 'config/i18n';
import { PLACE_TYPES } from 'views/motorcycle_rental/utils/constants';
import { geoPath } from 'views/motorcycle_rental/utils/helpers';

const {
  AIRPORT, REGION, COUNTRY, GEOGRAPHY,
} = PLACE_TYPES;

const NearbyRentalsWrapper = (props) => {
  const {
    nearbyRentals, handleNearByLinkClick, t, ssrRender,
  } = props;

  const rentalsIn = (name) => `${t('fe_es_geo_page:rentals')} ${name ? `${t('fe_es_geo_page:in')} ${name}` : ''}`;
  const rentalsNear = (name) => `${t('fe_es_geo_page:rentals')} ${name ? `${t('fe_es_geo_page:near')} ${name}` : ''}`;
  const isNearbyPlace = (placeType) => [PLACE_TYPES.AIRPORT, PLACE_TYPES.GEOGRAPHY, PLACE_TYPES.zipcode].includes(placeType);

  const rentalsInOrNear = (name, type) => (isNearbyPlace(type) ? rentalsNear(name) : rentalsIn(name));

  const prepareCountriesLinks = (countriesData) => {
    const rentalsName = countriesData.vehicle_type?.name || countriesData.vehicle_make?.name;
    const rentalsSlug = countriesData.vehicle_type?.slug || countriesData.vehicle_make?.slug;
    const title = `${rentalsName} ${t('fe_es_geo_page:rentals')} ${t('fe_es_geo_page:by')} ${t(
      'fe_es_geo_page:country',
    )}`;
    const linksList = countriesData?.countries.map((country, index) => ({
      id: index,
      path: geoPath([rentalsSlug, country.slug]),
      label: `${rentalsName} ${rentalsIn(country.name)}`,
    }));
    return {
      title,
      linksList,
    };
  };
  const getPlaceData = (spreadedPlaceData) => {
    let place;
    const {
      country, region, geography, airport,
    } = spreadedPlaceData;
    const places = {
      country,
      region,
      geography,
      airport,
    };
    Object.entries(places).forEach(([key, value]) => {
      if (value) {
        place = {
          ...value,
          type: key,
        };
      }
    });
    return place;
  };
  const prepareVehicleTypeOrMakeLinks = (vehicleTypesData, filterType) => {
    const place = getPlaceData(vehicleTypesData);
    let title = '';
    switch (place?.type) {
      case AIRPORT:
      case GEOGRAPHY:
        title = `${rentalsNear(place?.name)}`;
        break;
      case REGION:
      case COUNTRY:
      default:
        title = rentalsIn(place?.name);
        break;
    }
    title += ` ${t('fe_es_geo_page:by')} ${t(`rentals:${filterType}`)}`;
    const linksList = vehicleTypesData[`${filterType}s`].map((filter, index) => ({
      id: index,
      path: geoPath([filter.slug, place?.slug]),
      label: `${filter.name} ${
        isNearbyPlace(place?.type) ? rentalsNear(place?.name) : rentalsIn(place?.name)
      }`,
    }));
    return {
      title,
      linksList,
    };
  };
  const prepareVehicleModelLinks = (vehicleModelsData) => {
    let title;
    const place = getPlaceData(vehicleModelsData);
    const {
      model_first_type_name, vehicle_model, vehicle_make, vehicle_type, vehicle_models,
    } = vehicleModelsData;
    const typeOrMakeName = vehicle_make?.name || vehicle_type?.name;

    if (model_first_type_name && place) {
      title = `${t('fe_es_geo_page:similar')} ${model_first_type_name} ${rentalsInOrNear(
        place?.name,
        place?.type,
      )}`;
    } else if (model_first_type_name) {
      title = `${t('fe_es_geo_page:similar')} ${model_first_type_name} ${t(
        'fe_es_geo_page:rentals',
      )}`;
    } else if (vehicle_model && place) {
      title = `${t('fe_es_geo_page:similar')} ${
        vehicle_model.first_vehicle_type.name
      } ${rentalsInOrNear(place?.name, place?.type)}`;
    } else if (vehicle_model) {
      title = `${t('fe_es_geo_page:similar')} ${vehicle_model.first_vehicle_type.name} ${t(
        'fe_es_geo_page:rentals',
      )}`;
    } else if ([GEOGRAPHY, AIRPORT].includes(place?.type)) {
      title = `${typeOrMakeName} ${rentalsInOrNear(place?.name, place?.type)} ${t(
        'fe_es_geo_page:by',
      )} ${t('fe_es_geo_page:vehicle_model')}`;
    } else if (place && typeOrMakeName) {
      title = `${place?.name} ${typeOrMakeName} ${t('fe_es_geo_page:rentals')} ${t(
        'fe_es_geo_page:by',
      )} ${t('fe_es_geo_page:vehicle_model')}`;
    } else {
      title = `${typeOrMakeName} ${t('fe_es_geo_page:rentals')} ${t('fe_es_geo_page:by')} ${t(
        'fe_es_geo_page:vehicle_model',
      )}`;
    }

    const linksList = vehicle_models.map((vehicleModel, index) => ({
      id: index,
      path: geoPath([vehicleModel.vehicle_make.slug, vehicleModel.slug, place?.slug]),
      label: `${vehicleModel.name} ${rentalsIn(place?.name)}`,
    }));
    return {
      title,
      linksList,
    };
  };

  const prepareGeographyLinks = (geographiesData) => {
    let title;
    const place = getPlaceData(geographiesData);

    const {
      vehicle_model, vehicle_make, vehicle_type, geographies,
    } = geographiesData;
    const modelOrMakeOrTypeName = (vehicle_model || vehicle_make || vehicle_type)?.name;
    switch (place?.type) {
      case REGION:
        title = `${place?.name} ${modelOrMakeOrTypeName} ${t('fe_es_geo_page:rentals')} ${t(
          'fe_es_geo_page:by',
        )} ${t('fe_es_geo_page:city')}`;
        break;
      default:
        title = `${modelOrMakeOrTypeName} ${t('fe_es_geo_page:rentals')} ${t(
          'fe_es_geo_page:nearby',
        )} ${place?.name}`;
        break;
    }
    const getLabel = (geography) => {
      if (place && !(vehicle_make || vehicle_model || vehicle_type)) {
        return `${t('fe_es_geo_page:all_rentals')} ${t('fe_es_geo_page:in')} ${geography.name}`;
      }
      return `${modelOrMakeOrTypeName} ${rentalsNear(geography.name)}`;
    };
    const linksList = geographies.map((geography, index) => ({
      id: index,
      path: geoPath([vehicle_make?.slug, vehicle_type?.slug, vehicle_model?.slug, geography?.slug]),
      label: getLabel(geography),
    }));
    return {
      title,
      linksList,
    };
  };

  const prepareRegionsLinks = (regionsData) => {
    let title;
    const {
      regions, geographies, vehicle_type, vehicle_make, vehicle_model, country,
    } = regionsData;
    const modelOrMakeOrTypeName = (vehicle_model || vehicle_make || vehicle_type)?.name;

    let linksList = [];

    const getLabel = (region) => {
      if (country && !(vehicle_make || vehicle_model || vehicle_type)) {
        return `${t('fe_es_geo_page:all_rentals')} ${t('fe_es_geo_page:in')} ${region.name}`;
      }
      return `${modelOrMakeOrTypeName} ${rentalsIn(region.name)}`;
    };
    if (regions?.length > 0) {
      title = `${country?.name} ${modelOrMakeOrTypeName} ${t('fe_es_geo_page:rentals')} ${t(
        'fe_es_geo_page:by',
      )} ${t('fe_es_geo_page:region')}`;

      linksList = regions.map((region, index) => ({
        id: index,
        path: geoPath([vehicle_make?.slug, vehicle_type?.slug, vehicle_model?.slug, region.slug]),
        label: getLabel(region),
      }));
    } else {
      title = `${country?.name} ${modelOrMakeOrTypeName}  ${t('fe_es_geo_page:rentals')} ${t(
        'fe_es_geo_page:by',
      )} ${t('fe_es_geo_page:city')}`;

      linksList = geographies.map((geography, index) => ({
        id: index,
        path: geoPath([
          vehicle_make?.slug,
          vehicle_type?.slug,
          vehicle_model?.slug,
          geography.slug,
        ]),
        label: getLabel(geography),
      }));
    }

    return {
      title,
      linksList,
    };
  };
  const nearbyRentalsGroups = useMemo(() => {
    const rentalsGroups = [];
    if (nearbyRentals?.by_countries) {
      rentalsGroups.push(prepareCountriesLinks(nearbyRentals?.by_countries));
    }
    if (nearbyRentals?.by_vehicle_types) {
      rentalsGroups.push(
        prepareVehicleTypeOrMakeLinks(nearbyRentals?.by_vehicle_types, 'vehicle_type'),
      );
    }
    if (nearbyRentals?.by_vehicle_makes) {
      rentalsGroups.push(
        prepareVehicleTypeOrMakeLinks(nearbyRentals?.by_vehicle_makes, 'vehicle_make'),
      );
    }
    if (nearbyRentals?.by_vehicle_models) {
      rentalsGroups.push(prepareVehicleModelLinks(nearbyRentals?.by_vehicle_models));
    }
    if (nearbyRentals?.by_geographies) {
      rentalsGroups.push(prepareGeographyLinks(nearbyRentals?.by_geographies));
    }
    if (nearbyRentals?.by_regions) {
      rentalsGroups.push(prepareRegionsLinks(nearbyRentals?.by_regions));
    }
    return rentalsGroups;
  }, [nearbyRentals]);

  return (
    <Box>
      {nearbyRentalsGroups.map((rentalsGroup) => (
        <LinksGroupSection
          key={rentalsGroup.title}
          linksGroup={rentalsGroup}
          handleLinkClick={handleNearByLinkClick}
          defaultExpanded={ssrRender}
        />
      ))}
    </Box>
  );
};
export default withTranslation(['fe_es_geo_page'])(NearbyRentalsWrapper);
